import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Environment } from '@r1-compass-library/environments/environment';
import { IamDivision } from '@r1-compass-library/models/iam/iam-division.model';
import { Observable, catchError, finalize, of, retry } from 'rxjs';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { SpinnerService } from './spinner.service';

@Injectable({ providedIn: 'root' })
export class IamDivisionService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private BASE_URL: string = Environment.baseUrl + '/frontend/iam-divisions';

    get(id: number): Observable<IamDivision> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamDivisionService.get';
        this.#spinnerService.addProcess(process, 'Getting IAM division...');
        return this.#http.get<IamDivision>(`${this.BASE_URL}/${id}`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<IamDivision>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    create(iamDivision: Partial<IamDivision>): Observable<IamDivision> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamDivisionService.create';
        this.#spinnerService.addProcess(process, 'Creating IAM division...');
        return this.#http.post<IamDivision>(`${this.BASE_URL}`, iamDivision, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamDivision>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    update(id: number, iamDivision: Partial<IamDivision>): Observable<IamDivision> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
            }),
        };
        const process = 'IamDivisionService.update';
        this.#spinnerService.addProcess(process, 'Updating IAM division...');
        return this.#http.patch<IamDivision>(`${this.BASE_URL}/${id}`, iamDivision, httpOptions).pipe(
            catchError(error => {
                return of<IamDivision>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    findAll(): Observable<IamDivision[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamDivisionService.findAll';
        this.#spinnerService.addProcess(process, 'Getting IAM divisions...');
        return this.#http.get<IamDivision[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamDivision[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }
}
